import React, { useMemo, useState } from 'react';
import {
  hesaplaTumAlacaklar, brutToNet, netToBrut,
  parseTutar, formatTL, getKidemTavani, ihbarHaftasi, kidemSuresi,
  GELIR_VERGISI_TARIFESI, SGK_ISCI_ORANI, ISSIZLIK_ISCI_ORANI,
  DAMGA_ORANI, ASGARI_BRUT_2026, SGK_TAVAN_2026,
} from '../../utils/iscilikAlacaklari';
import { iscilikPdfUret } from '../../utils/iscilikPdf';
import { iscilikWordUret } from '../../utils/iscilikWord';
import { documentAPI, caseFileAPI } from '../../api/api';
import './IscilikHesap.css';

const bugun = () => new Date().toISOString().slice(0, 10);

/**
 * İşçilik alacakları hesap bileşeni.
 * Hem tam sayfa (Hesaplama menüsü) hem de dosya formunda açılır
 * panel olarak kullanılır.
 *
 * @param {object}   caseFile   Dosya (varsa alanlar önden dolar)
 * @param {boolean}  embedded   Dosya formu içinde kompakt görünüm
 * @param {function} onKapat    Panel kapatma (embedded)
 */
const IscilikHesap = ({ caseFile = null, embedded = false, onKapat = null }) => {
  const cf = caseFile || {};
  const g = (a, b) => cf[a] ?? cf[b] ?? '';

  const [ucretTipi, setUcretTipi] = useState('brut'); // brut | net
  const [ciplak, setCiplak] = useState(String(g('SonUcret', 'sonUcret') || ''));
  const [giydirilmis, setGiydirilmis] = useState('');
  const [giris, setGiris] = useState(String(g('IseGirisTarihi', 'iseGirisTarihi') || '').slice(0, 10));
  const [cikis, setCikis] = useState(String(g('IstenCikisTarihi', 'istenCikisTarihi') || '').slice(0, 10) || bugun());

  const [kidem, setKidem] = useState(true);
  const [ihbar, setIhbar] = useState(true);
  const [izinGun, setIzinGun] = useState('');
  const [fmSaat, setFmSaat] = useState('');
  const [ubgtGun, setUbgtGun] = useState('');
  const [htGun, setHtGun] = useState('');
  // Gün sayımı yöntemi: uçlar hariç (yaygın bilirkişi) / dahil
  const [uclarDahil, setUclarDahil] = useState(false);
  const [paramAcik, setParamAcik] = useState(false);
  // Belge biçimi. Word UTF-8 tabanlı olduğu için Türkçe karakterleri
  // kusursuz taşır; PDF'te ğ/ş/İ/ı sadeleşiyor. Varsayılan: Word.
  const [bicim, setBicim] = useState('word'); // word | pdf

  const [cevirGiris, setCevirGiris] = useState('');
  const [cevirYon, setCevirYon] = useState('brutNet'); // brutNet | netBrut

  const [durum, setDurum] = useState(null); // {tip, metin}
  const [yukleniyor, setYukleniyor] = useState(false);

  /* --- Tam sayfa kullanımda dosya seçimi (aktarma hedefi) --- */
  const [dosyalar, setDosyalar] = useState([]);
  const [dosyaAra, setDosyaAra] = useState('');
  const [secilenDosyaId, setSecilenDosyaId] = useState('');
  const [dosyalarYukleniyor, setDosyalarYukleniyor] = useState(false);

  const dosyalariGetir = async () => {
    if (dosyalar.length || dosyalarYukleniyor) return;
    setDosyalarYukleniyor(true);
    try {
      const liste = await caseFileAPI.getAll();
      setDosyalar(Array.isArray(liste) ? liste : []);
    } catch (e) {
      setDurum({ tip: 'hata', metin: 'Dosya listesi alınamadı: ' + (e?.message || '') });
    } finally { setDosyalarYukleniyor(false); }
  };

  /**
   * Föy no veya müvekkil adına göre süzer.
   * Türkçe'ye özgü büyük/küçük harf kuralları için toLocaleLowerCase('tr')
   * kullanılıyor — aksi halde "İ" ve "I" aramada eşleşmiyor.
   */
  const filtreliDosyalar = useMemo(() => {
    const q = dosyaAra.trim().toLocaleLowerCase('tr');
    const sirala = (a, b) =>
      Number(b.FoyNo ?? b.foyNo ?? 0) - Number(a.FoyNo ?? a.foyNo ?? 0);
    if (!q) return [...dosyalar].sort(sirala).slice(0, 100);
    return dosyalar
      .filter((d) => {
        const foy = String(d.FoyNo ?? d.foyNo ?? '');
        const mv = String(d.Muvekkil ?? d.muvekkil ?? '').toLocaleLowerCase('tr');
        return foy.includes(q) || mv.includes(q);
      })
      .sort(sirala)
      .slice(0, 100);
  }, [dosyalar, dosyaAra]);

  /* --- Girilen ücret net ise brüte çevir --- */
  const ciplakBrut = useMemo(() => {
    const v = parseTutar(ciplak);
    if (v <= 0) return 0;
    if (ucretTipi === 'brut') return v;
    const r = netToBrut(v);
    return r ? r.brut : 0;
  }, [ciplak, ucretTipi]);

  const giydirilmisBrut = useMemo(() => {
    const v = parseTutar(giydirilmis);
    if (v > 0) return ucretTipi === 'brut' ? v : (netToBrut(v)?.brut || 0);
    return ciplakBrut;
  }, [giydirilmis, ucretTipi, ciplakBrut]);

  const sonuc = useMemo(() => {
    if (!ciplakBrut || !giris || !cikis) return null;
    return hesaplaTumAlacaklar({
      ciplakAylikBrut: ciplakBrut,
      giydirilmisAylikBrut: giydirilmisBrut,
      giris, cikis, kidem, ihbar,
      yillikIzinGun: parseTutar(izinGun),
      fazlaMesaiSaat: parseTutar(fmSaat),
      ubgtGun: parseTutar(ubgtGun),
      haftaTatiliGun: parseTutar(htGun),
      uclarDahil,
    });
  }, [ciplakBrut, giydirilmisBrut, giris, cikis, kidem, ihbar, izinGun, fmSaat, ubgtGun, htGun, uclarDahil]);

  /* --- Brüt ↔ Net çevirici --- */
  const cevrim = useMemo(() => {
    const v = parseTutar(cevirGiris);
    if (v <= 0) return null;
    return cevirYon === 'brutNet' ? brutToNet(v) : netToBrut(v);
  }, [cevirGiris, cevirYon]);

  const sure = giris && cikis ? kidemSuresi(giris, cikis, uclarDahil) : null;

  /* --- PDF --- */
  const pdfBilgi = () => ({
    foyNo: g('FoyNo', 'foyNo'),
    muvekkil: g('Muvekkil', 'muvekkil'),
    isveren: g('IsverenFirma', 'isverenFirma'),
    giris, cikis,
    ciplak: ciplakBrut,
    giydirilmis: giydirilmisBrut,
  });

  /** Seçilen biçimde belge üretir. */
  const belgeUret = async () => (
    bicim === 'word'
      ? { ...iscilikWordUret(sonuc, pdfBilgi()), unicode: true }
      : await iscilikPdfUret(sonuc, pdfBilgi())
  );

  const indir = async () => {
    if (!sonuc) return;
    setYukleniyor(true);
    try {
      const { blob, dosyaAdi } = await belgeUret();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = dosyaAdi; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      setDurum({ tip: 'ok', metin: (bicim === 'word' ? 'Word belgesi' : 'PDF') + ' indirildi.' });
    } catch (e) {
      setDurum({ tip: 'hata', metin: 'Belge oluşturulamadı: ' + (e?.message || '') });
    } finally { setYukleniyor(false); }
  };

  const dosyayaAktar = async () => {
    // Gömülü kullanımda dosya zaten belli; tam sayfada seçilen dosya
    const id = (cf.Id ?? cf.id) || secilenDosyaId;
    if (!id) { setDurum({ tip: 'hata', metin: 'Önce bir dosya seçin.' }); return; }
    if (!sonuc) return;
    setYukleniyor(true);
    setDurum({ tip: 'bilgi', metin: 'Belge hazırlanıyor…' });
    try {
      const { blob, dosyaAdi, unicode } = await belgeUret();
      const file = new File([blob], dosyaAdi, { type: bicim === 'word' ? 'application/msword' : 'application/pdf' });
      await documentAPI.upload(id, file, 'İşçilik alacakları hesap tablosu', 0);
      setDurum({
        tip: 'ok',
        metin: 'Dosyanın belgeler bölümüne eklendi.' + (unicode ? '' : ' (Türkçe karakterler sadeleştirildi)'),
      });
    } catch (e) {
      setDurum({ tip: 'hata', metin: 'Yüklenemedi: ' + (e?.response?.data?.message || e?.message || '') });
    } finally { setYukleniyor(false); }
  };

  const dosyaVar = !!(cf.Id ?? cf.id);

  return (
    <div className={`ih ${embedded ? 'ih-embedded' : ''}`}>
      {embedded && (
        <div className="ih-embed-head">
          <span>İşçilik Alacağı Hesapla</span>
          {onKapat && <button type="button" className="ih-kapat" onClick={onKapat}>Kapat</button>}
        </div>
      )}

      <div className="ih-grid">
        {/* ---------------- Girdi ---------------- */}
        <div className="ih-card">
          <h3 className="ih-baslik">Ücret ve Süre</h3>

          <div className="ih-segment">
            <button type="button" className={ucretTipi === 'brut' ? 'on' : ''} onClick={() => setUcretTipi('brut')}>Brüt girerim</button>
            <button type="button" className={ucretTipi === 'net' ? 'on' : ''} onClick={() => setUcretTipi('net')}>Net girerim</button>
          </div>

          <label className="ih-lbl">Çıplak aylık ücret ({ucretTipi === 'brut' ? 'brüt' : 'net'})</label>
          <input className="ih-inp" inputMode="decimal" value={ciplak} onChange={(e) => setCiplak(e.target.value)} placeholder="Örn. 45.000" />
          {ucretTipi === 'net' && ciplakBrut > 0 && (
            <div className="ih-mini">≈ brüt {formatTL(ciplakBrut)} TL</div>
          )}

          <label className="ih-lbl">Giydirilmiş ücret <span className="ih-ops">(boşsa çıplak ücret kullanılır)</span></label>
          <input className="ih-inp" inputMode="decimal" value={giydirilmis} onChange={(e) => setGiydirilmis(e.target.value)} placeholder="Yol, yemek, ikramiye dahil" />

          <div className="ih-2">
            <div>
              <label className="ih-lbl">İşe giriş</label>
              <input type="date" className="ih-inp" value={giris} onChange={(e) => setGiris(e.target.value)} />
            </div>
            <div>
              <label className="ih-lbl">İşten çıkış</label>
              <input type="date" className="ih-inp" value={cikis} onChange={(e) => setCikis(e.target.value)} />
            </div>
          </div>

          {sure && sure.toplamGun > 0 && (
            <div className="ih-mini">
              Çalışma süresi <b>{sure.yil} yıl {sure.kalanGun} gün</b> ({sure.toplamGun} gün) ·
              ihbar <b>{ihbarHaftasi(giris, cikis)} hafta</b> ·
              kıdem tavanı <b>{formatTL(getKidemTavani(cikis))} TL</b>
            </div>
          )}

          {/* Gün sayımı yöntemi — hesaplayıcılar burada ayrışıyor */}
          <label className="ih-chk ih-chk-ince">
            <input type="checkbox" checked={uclarDahil} onChange={(e) => setUclarDahil(e.target.checked)} />
            Giriş ve çıkış günü de sayılsın (+1 gün)
          </label>

          <h3 className="ih-baslik ih-mt">Talep Kalemleri</h3>
          <label className="ih-chk"><input type="checkbox" checked={kidem} onChange={(e) => setKidem(e.target.checked)} /> Kıdem tazminatı</label>
          <label className="ih-chk"><input type="checkbox" checked={ihbar} onChange={(e) => setIhbar(e.target.checked)} /> İhbar tazminatı</label>

          <div className="ih-2">
            <div><label className="ih-lbl">Yıllık izin (gün)</label><input className="ih-inp" inputMode="numeric" value={izinGun} onChange={(e) => setIzinGun(e.target.value)} /></div>
            <div><label className="ih-lbl">Fazla mesai (saat)</label><input className="ih-inp" inputMode="numeric" value={fmSaat} onChange={(e) => setFmSaat(e.target.value)} /></div>
          </div>
          <div className="ih-2">
            <div><label className="ih-lbl">UBGT (gün)</label><input className="ih-inp" inputMode="numeric" value={ubgtGun} onChange={(e) => setUbgtGun(e.target.value)} /></div>
            <div><label className="ih-lbl">Hafta tatili (gün)</label><input className="ih-inp" inputMode="numeric" value={htGun} onChange={(e) => setHtGun(e.target.value)} /></div>
          </div>
        </div>

        {/* ---------------- Sonuç ---------------- */}
        <div className="ih-card">
          <h3 className="ih-baslik">Sonuç</h3>

          {!sonuc ? (
            <p className="ih-bos">Ücret ve tarihleri girin.</p>
          ) : (
            <>
              {/* Brüt ve net BİRLİKTE gösterilir: başka hesaplayıcıların
                  çoğu brüt toplam verir, karşılaştırırken karışıyordu. */}
              <div className="ih-toplam">
                <div className="ih-toplam-kutu">
                  <span>Toplam Brüt</span>
                  <strong>{formatTL(sonuc.toplam.brut)} TL</strong>
                </div>
                <div className="ih-toplam-kutu ih-toplam-net">
                  <span>Toplam Net</span>
                  <strong>{formatTL(sonuc.toplam.net)} TL</strong>
                </div>
              </div>

              <div className="ih-tabloSar">
                <table className="ih-tablo">
                  <thead>
                    <tr><th>Kalem</th><th className="s">Brüt</th><th className="s">Kesinti</th><th className="s">Net</th></tr>
                  </thead>
                  <tbody>
                    {sonuc.kalemler.map((k, i) => (
                      <tr key={i}>
                        <td>
                          {k.tur}
                          {k.aciklama && <span className="ih-acik">{k.aciklama}</span>}
                          {k.tavanUygulandi && <span className="ih-uyari">tavan uygulandı</span>}
                        </td>
                        <td className="s">{formatTL(k.brut)}</td>
                        <td className="s">{formatTL((k.gelirVergisi || 0) + (k.damga || 0))}</td>
                        <td className="s b">{formatTL(k.net)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>TOPLAM</td>
                      <td className="s">{formatTL(sonuc.toplam.brut)}</td>
                      <td className="s">{formatTL(sonuc.toplam.gelirVergisi + sonuc.toplam.damga)}</td>
                      <td className="s b">{formatTL(sonuc.toplam.net)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Tam sayfa kullanımda hedef dosya seçimi */}
              {!dosyaVar && (
                <div className="ih-dosyasec">
                  <label className="ih-lbl">Hesabı aktaracağınız dosya</label>
                  <input
                    className="ih-inp"
                    placeholder="Föy no veya müvekkil adıyla ara…"
                    value={dosyaAra}
                    onFocus={dosyalariGetir}
                    onChange={(e) => { setDosyaAra(e.target.value); dosyalariGetir(); }}
                  />
                  <select
                    className="ih-inp ih-dosyasec-liste"
                    value={secilenDosyaId}
                    onFocus={dosyalariGetir}
                    onChange={(e) => setSecilenDosyaId(e.target.value)}
                  >
                    <option value="">
                      {dosyalarYukleniyor ? 'Dosyalar yükleniyor…' : 'Dosya seçin'}
                    </option>
                    {filtreliDosyalar.map((d) => {
                      const id = d.Id ?? d.id;
                      return (
                        <option key={id} value={id}>
                          {(d.FoyNo ?? d.foyNo) ?? '-'} — {(d.Muvekkil ?? d.muvekkil) || 'İsimsiz'}
                        </option>
                      );
                    })}
                  </select>
                </div>
              )}

              <div className="ih-bicim">
                <span className="ih-bicim-lbl">Belge biçimi</span>
                <div className="ih-segment ih-segment-sm">
                  <button type="button" className={bicim === 'word' ? 'on' : ''} onClick={() => setBicim('word')}>Word</button>
                  <button type="button" className={bicim === 'pdf' ? 'on' : ''} onClick={() => setBicim('pdf')}>PDF</button>
                </div>
                {bicim === 'pdf' && (
                  <span className="ih-bicim-not">PDF'te ğ, ş, İ, ı harfleri sadeleşir</span>
                )}
              </div>

              <div className="ih-aksiyon">
                <button type="button" className="ih-btn" onClick={indir} disabled={yukleniyor}>
                  {bicim === 'word' ? 'Word indir' : 'PDF indir'}
                </button>
                <button
                  type="button"
                  className="ih-btn ih-btn-ana"
                  onClick={dosyayaAktar}
                  disabled={yukleniyor || (!dosyaVar && !secilenDosyaId)}
                >
                  {embedded ? 'Dosyaya kaydet' : 'Dosyaya aktar'}
                </button>
              </div>
              {durum && <div className={`ih-durum ih-${durum.tip}`}>{durum.metin}</div>}
            </>
          )}
        </div>
      </div>

      {/* ---------------- Brüt ↔ Net çevirici ---------------- */}
      <div className="ih-card ih-cevirici">
        <h3 className="ih-baslik">Brüt ↔ Net Çevirici</h3>
        <div className="ih-cev-satir">
          <div className="ih-segment ih-segment-sm">
            <button type="button" className={cevirYon === 'brutNet' ? 'on' : ''} onClick={() => setCevirYon('brutNet')}>Brüt → Net</button>
            <button type="button" className={cevirYon === 'netBrut' ? 'on' : ''} onClick={() => setCevirYon('netBrut')}>Net → Brüt</button>
          </div>
          <input
            className="ih-inp"
            inputMode="decimal"
            value={cevirGiris}
            onChange={(e) => setCevirGiris(e.target.value)}
            placeholder={cevirYon === 'brutNet' ? 'Brüt tutar' : 'Net tutar'}
          />
        </div>

        {cevrim && (
          <div className="ih-cev-sonuc">
            <div className="ih-cev-kutu">
              <span>Brüt</span><strong>{formatTL(cevrim.brut)} TL</strong>
            </div>
            <div className="ih-cev-kutu">
              <span>Net</span><strong>{formatTL(cevrim.net)} TL</strong>
            </div>
            <div className="ih-cev-detay">
              SGK {formatTL(cevrim.sgk)} · İşsizlik {formatTL(cevrim.issizlik)} ·
              Gelir V. {formatTL(cevrim.gelirVergisi)} · Damga {formatTL(cevrim.damga)}
              {cevrim.gelirVergisiIstisna > 0 && (
                <> · asgari ücret istisnası {formatTL(cevrim.gelirVergisiIstisna + cevrim.damgaIstisna)}</>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ---------------- Hesap parametreleri ---------------- */}
      <div className="ih-card ih-param">
        <button type="button" className="ih-param-ac" onClick={() => setParamAcik((v) => !v)}>
          Hesapta Kullanılan Parametreler {paramAcik ? '▲' : '▼'}
        </button>

        {paramAcik && (
          <div className="ih-param-icerik">
            <div className="ih-param-grid">
              <div className="ih-param-kutu">
                <span>Kıdem tavanı</span>
                <strong>{formatTL(getKidemTavani(cikis))} TL</strong>
                <em>{(parseTutar(String(cikis).slice(5, 7)) >= 7) ? '01.07–31.12.2026' : '01.01–30.06.2026'}</em>
              </div>
              <div className="ih-param-kutu">
                <span>Asgari ücret (brüt)</span>
                <strong>{formatTL(ASGARI_BRUT_2026)} TL</strong>
                <em>net 28.075,50 TL</em>
              </div>
              <div className="ih-param-kutu">
                <span>SGK işçi payı</span>
                <strong>%{SGK_ISCI_ORANI}</strong>
                <em>+ işsizlik %{ISSIZLIK_ISCI_ORANI}</em>
              </div>
              <div className="ih-param-kutu">
                <span>Damga vergisi</span>
                <strong>binde 7,59</strong>
                <em>{DAMGA_ORANI}</em>
              </div>
              <div className="ih-param-kutu">
                <span>SGK tavanı</span>
                <strong>{formatTL(SGK_TAVAN_2026)} TL</strong>
                <em>aylık matrah üst sınırı</em>
              </div>
            </div>

            <div className="ih-param-vergi">
              <span className="ih-param-alt">Gelir vergisi tarifesi (ücret · 2026)</span>
              <table className="ih-tablo ih-tablo-ince">
                <tbody>
                  {GELIR_VERGISI_TARIFESI[2026].map((d, i, arr) => {
                    const alt = i === 0 ? 0 : arr[i - 1].ustSinir;
                    return (
                      <tr key={i}>
                        <td>
                          {Number.isFinite(d.ustSinir)
                            ? `${formatTL(alt)} – ${formatTL(d.ustSinir)} TL`
                            : `${formatTL(alt)} TL üzeri`}
                        </td>
                        <td className="s b">%{d.oran}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <ul className="ih-param-notlar">
              <li>Kıdem tazminatından yalnızca <b>damga vergisi</b> kesilir; gelir vergisi ve SGK primi alınmaz.</li>
              <li>İhbar, yıllık izin, fazla mesai, UBGT ve hafta tatilinden <b>gelir vergisi + damga vergisi</b> kesilir.</li>
              <li>Giydirilmiş ücret kıdem tavanını aşarsa hesap <b>tavandan</b> yapılır (1475 s.K. m.14).</li>
              <li>Fazla mesai saatlik ücreti <b>aylık brüt / 225</b> esasıyla, %50 zamlı hesaplanır.</li>
              <li>Hafta tatili <b>%150</b>, UBGT <b>1 günlük ilave</b> ücret olarak alınır.</li>
              <li>Net ücret girildiğinde brüte çevirme <b>asgari ücret istisnası</b> uygulanarak yapılır (7349 s.K.).</li>
            </ul>
          </div>
        )}
      </div>

      <p className="ih-kaynak">
        Kaynaklar: gelir vergisi tarifesi 31.12.2025 tarihli Resmî Gazete (5. mükerrer) · kıdem tavanı ilgili dönem ·
        SGK ve damga oranları 2026 bordro parametreleri. Bilgilendirme amaçlıdır; bilirkişi raporu yerine geçmez.
      </p>
    </div>
  );
};

export default IscilikHesap;
